import { GetStaticPropsResult } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import { ContactForm, Layout } from '../components';
import { i18nConfig } from '../next-i18next.config';
import { ContactsProps, Locale } from '../types';
import getPrismicMenuByLocale from '../utils/prismic/menu';
import getPrismicOptionsByLocale from '../utils/prismic/options';
import { IconMail, IconPhone, IconHome } from '@tabler/icons-react';
import Link from 'next/link';
import { REVALIDATION_TIME } from '../utils/constants';

function ContactPage(props: ContactsProps): JSX.Element {
  const { t } = useTranslation('contact');

  return (
    <>
      <NextSeo title="Prickle | Contact" description="Prickle | Contact" />
      <Layout options={props.options} menu={props.menu}>
        <div className="relative h-full bg-dark font-body">
          <div className="h-full gap-16 px-8 pt-16 pb-16 sm:pt-28 sm:px-6 lg:px-8 lg:max-w-7xl lg:mx-auto lg:grid lg:grid-cols-2">
            <div className="h-full lg:pr-8">
              <div className="flex flex-col h-full max-w-md mx-auto sm:max-w-lg lg:mx-0 font-body">
                <div>
                  <h2 className="text-4xl font-bold leading-none text-white font-heading sm:text-6xl">
                    {t('contacttitle')}
                  </h2>
                  <p className="mt-8 text-lg leading-7 text-white ">
                    {t('contactsubtitle')}
                  </p>
                </div>
                <div className="my-12">
                  <div className="px-6 py-6 transition-colors duration-300 rounded-md cursor-pointer md:w-max-content text-primary hover:text-white hover:bg-primaryLighter hover:border-primary">
                    <dt className="sr-only">Email</dt>
                    <dd className="flex">
                      <IconMail size={24} />
                      <span className="ml-3 text-white">
                        <Link href="mailto:hr@prickle.io" as="next-link">
                          contact@prickle.io
                        </Link>
                      </span>
                    </dd>
                  </div>
                  <div className="px-6 py-6 transition-colors duration-300 rounded-md cursor-pointer md:w-max-content text-primary hover:text-white hover:bg-primaryLighter hover:border-primary">
                    <dt className="sr-only">Phone number</dt>
                    <dd className="flex">
                      <IconPhone size={24} />
                      <span className="ml-3 text-white">
                        <Link href="tel:+31631687331" as="next-link">
                          +31 63 1687 331
                        </Link>
                      </span>
                    </dd>
                  </div>
                  <div className="px-6 py-6 transition-colors duration-300 rounded-md cursor-pointer md:w-max-content text-primary hover:text-white hover:bg-primaryLighter hover:border-primary">
                    <dt className="sr-only">Phone number</dt>
                    <dd className="flex">
                      <IconPhone size={24} />
                      <span className="ml-3 text-white">
                        <Link href="tel:+31611125103" as="next-link">
                          +31 61 1125 103
                        </Link>
                      </span>
                    </dd>
                  </div>
                  <div className="px-6 py-6 transition-colors duration-300 rounded-md cursor-pointer md:w-max-content text-primary hover:text-white hover:bg-primaryLighter hover:border-primary">
                    <dt className="sr-only">Location</dt>
                    <dd className="flex">
                      <IconHome size={24} />
                      <span className="ml-3 text-white">
                        Amsterdam, Netherlands
                      </span>
                    </dd>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3 my-6 justify-evenly">
                  <span className="inline-flex rounded-md shadow-sm w-max-content">
                    <Link
                      as="a"
                      target="_blank"
                      href="https://www.facebook.com/prickle.io"
                      className="inline-flex items-center justify-center w-16 h-16 text-sm font-medium leading-5 transition duration-300 ease-in-out rounded-full cursor-pointer text-primary hover:text-white hover:bg-primary focus:outline-none focus:bg-primary focus:shadow-outline-blue"
                      rel="noreferrer"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M20 10c0-5.523-4.477-10-10-10S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.988C16.343 19.128 20 14.991 20 10z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </Link>
                  </span>

                  <span className="inline-flex shadow-sm w-max-content">
                    <Link
                      as="a"
                      href="https://twitter.com/prickle_io"
                      target="_blank"
                      className="inline-flex items-center justify-center w-16 h-16 text-sm font-medium leading-5 transition duration-300 ease-in-out rounded-full cursor-pointer text-primary hover:text-white hover:bg-primary focus:outline-none focus:bg-primary focus:shadow-outline-blue"
                      rel="noreferrer"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
                      </svg>
                    </Link>
                  </span>

                  <span className="inline-flex shadow-sm cursor-pointer w-max-content">
                    <Link
                      as="a"
                      href="https://www.linkedin.com/company/prickle/"
                      target="_blank"
                      className="inline-flex items-center justify-center w-16 h-16 text-sm font-medium leading-5 transition duration-300 ease-in-out rounded-full cursor-pointer text-primary hover:text-white hover:bg-primary focus:outline-none focus:bg-primary focus:shadow-outline-blue"
                      rel="noreferrer"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                      </svg>
                    </Link>
                  </span>
                  <span className="inline-flex shadow-sm cursor-pointer w-max-content">
                    <Link
                      as="a"
                      href="https://github.com/prickle-io"
                      target="_blank"
                      className="inline-flex items-center justify-center w-16 h-16 text-sm font-medium leading-5 transition duration-300 ease-in-out rounded-full cursor-pointer text-primary hover:text-white hover:bg-primary focus:outline-none focus:bg-primary focus:shadow-outline-blue"
                      rel="noreferrer"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </Link>
                  </span>
                </div>
              </div>
            </div>
            <div className="px-8 py-12 bg-white rounded-md shadow sm:rounded-md sm:overflow-hidden lg:p-16">
              <ContactForm />
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
}

export const getStaticProps = async ({
  locale,
  previewData,
}: {
  locale: Locale;
  previewData: any;
}): Promise<GetStaticPropsResult<ContactsProps>> => {
  const options = await getPrismicOptionsByLocale(locale, previewData);
  const menu = await getPrismicMenuByLocale(locale, previewData);
  return {
    revalidate: REVALIDATION_TIME,
    props: {
      ...(await serverSideTranslations(
        locale,
        ['contact', 'common'],
        i18nConfig,
      )),
      menu,
      options,
    },
  };
};

export default ContactPage;
